import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import FileUploadField from "@/components/FileUploadField";
import { toast } from "sonner";
import { Plus, Send, Trash2 } from "lucide-react";
import BackButton from "@/components/BackButton";

function StatusBadge({ status }) {
  const map = {
    DRAFT: "bg-secondary text-secondary-foreground",
    PENDING_APPROVAL: "bg-yellow-100 text-yellow-900 border-yellow-200",
    APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-200",
    REJECTED: "bg-red-100 text-red-900 border-red-200",
    ACKNOWLEDGED: "bg-blue-100 text-blue-900 border-blue-200",
    PARTIALLY_RECEIVED: "bg-amber-100 text-amber-900 border-amber-200",
    FULFILLED: "bg-emerald-100 text-emerald-900 border-emerald-200",
    POSTED: "bg-emerald-100 text-emerald-900 border-emerald-200",
    SUBMITTED: "bg-blue-100 text-blue-900 border-blue-200",
  };
  return <Badge variant="outline" className={`${map[status] || ""} font-mono text-[10px]`}>{status}</Badge>;
}

export default function Requisitions() {
  const { user } = useAuth();
  const canSeePrice = !!(user?.price_access || user?.role === "ORG_ADMIN" || user?.role === "PROCUREMENT_MANAGER" || user?.role === "PURCHASE_MANAGER" || user?.role === "FINANCE_MANAGER");

  const [items, setItems] = useState([]);
  const [itemsMaster, setItemsMaster] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  function blank() {
    return { department: "", needed_by: "", notes: "", template_url: "", lines: [emptyLine()] };
  }
  function emptyLine() { return { item_id: "", item_name: "", description: "", quantity: 1, unit: "unit", estimated_price: 0 }; }

  const load = async () => {
    const [pr, im, dp] = await Promise.all([
      api.get("/procurement/requisitions"),
      api.get("/inventory/items").catch(() => ({ data: [] })),
      api.get("/org/departments-v2").catch(() => ({ data: [] })),
    ]);
    setItems(pr.data); setItemsMaster(im.data); setDepartments(dp.data);
  };
  useEffect(() => { load(); }, []);

  const total = form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.estimated_price || 0), 0);

  const submit = async () => {
    if (!form.department) { toast.error("Department is required"); return; }
    try {
      const payload = {
        title: `${form.department} requisition · ${new Date().toLocaleDateString()}`,
        ...form,
        lines: form.lines.map((l) => ({ ...l, quantity: Number(l.quantity), estimated_price: Number(l.estimated_price) })),
      };
      await api.post("/procurement/requisitions", payload);
      toast.success("Requisition created — submit it for approval");
      setOpen(false); setForm(blank()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitForApproval = async (id) => {
    try {
      const { data } = await api.post(`/procurement/requisitions/${id}/submit`);
      toast.success(`Submitted (${data.status})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateLine = (idx, k, v) => {
    const lines = [...form.lines]; lines[idx] = { ...lines[idx], [k]: v }; setForm({ ...form, lines });
  };
  const pickItem = (idx, itemId) => {
    const it = itemsMaster.find((x) => x.id === itemId);
    if (!it) return;
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx],
      item_id: itemId, item_name: it.name, unit: it.unit,
      estimated_price: it.standard_cost || it.sale_price || 0,
    };
    setForm({ ...form, lines });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="requisitions-page">
      <BackButton />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Purchase requisitions</h1>
          <p className="text-muted-foreground">Internal requests for goods or services. Multi-level approval applies based on value.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-pr-btn"><Plus className="w-4 h-4 mr-1" /> New PR</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pr-dialog">
            <DialogHeader><DialogTitle>New purchase requisition</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                    <SelectTrigger data-testid="pr-dept-select"><SelectValue placeholder="Pick department" /></SelectTrigger>
                    <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Needed by</Label><Input type="date" value={form.needed_by} onChange={(e) => setForm({ ...form, needed_by: e.target.value })} data-testid="pr-date-input" /></div>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

              <div className="border border-border rounded-md">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
                  <div className="label-overline">Line items</div>
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })} data-testid="pr-add-line-btn">+ Add line</Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead><TableHead>Qty</TableHead><TableHead>Unit</TableHead>
                      {canSeePrice ? <TableHead>Est. price</TableHead> : null}
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.lines.map((l, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Select value={l.item_id || ""} onValueChange={(v) => pickItem(idx, v)}>
                            <SelectTrigger data-testid={`pr-line-item-${idx}`}><SelectValue placeholder="Pick item" /></SelectTrigger>
                            <SelectContent>
                              {itemsMaster.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="w-20"><Input type="number" value={l.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} data-testid={`pr-line-qty-${idx}`} /></TableCell>
                        <TableCell className="w-24"><Input value={l.unit} onChange={(e) => updateLine(idx, "unit", e.target.value)} /></TableCell>
                        {canSeePrice ? (
                          <TableCell className="w-28"><Input type="number" value={l.estimated_price} onChange={(e) => updateLine(idx, "estimated_price", e.target.value)} data-testid={`pr-line-price-${idx}`} /></TableCell>
                        ) : null}
                        <TableCell>
                          {form.lines.length > 1 && (
                            <button onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })} className="text-muted-foreground hover:text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {canSeePrice ? (
                  <div className="px-3 py-2 border-t border-border flex justify-between items-center">
                    <span className="label-overline">Estimated total</span>
                    <span className="font-display font-black">₹{total.toLocaleString()}</span>
                  </div>
                ) : null}
              </div>
              <Button onClick={submit} data-testid="pr-create-btn">Create requisition</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PR #</TableHead><TableHead>Department</TableHead>
              {canSeePrice ? <TableHead>Total</TableHead> : null}
              <TableHead>Status</TableHead><TableHead>Approval</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`pr-row-${i.id}`}>
                <TableCell className="font-mono text-xs">{i.number}</TableCell>
                <TableCell>{i.department || "—"}</TableCell>
                {canSeePrice ? <TableCell className="font-mono">₹{Number(i.estimated_total || 0).toLocaleString()}</TableCell> : null}
                <TableCell><StatusBadge status={i.status} /></TableCell>
                <TableCell className="text-xs font-mono">{i.approval_state?.current_level || 0}/{i.approval_state?.total_levels || 0}</TableCell>
                <TableCell>
                  {(i.status === "DRAFT" || i.status === "REJECTED") && (
                    <Button size="sm" variant="outline" onClick={() => submitForApproval(i.id)} data-testid={`pr-submit-${i.id}`}><Send className="w-3 h-3 mr-1" /> Submit</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={canSeePrice ? 6 : 5} className="text-center text-muted-foreground py-8">No requisitions yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
